import type { AxiosError } from "axios";
import { type ReactNode, useLayoutEffect, useRef } from "react";
import { toast } from "sonner";
import { axiosPrivate } from "../api/axios";
import useAuth from "./useAuth";
import useRefreshToken from "./useRefreshToken";

const HANDLED_BY_AXIOS_PRIVATE = "handledByAxiosPrivate";

type HandledAxiosError = AxiosError & {
	[HANDLED_BY_AXIOS_PRIVATE]?: boolean;
};

const getErrorMessage = (data: unknown) => {
	if (typeof data === "string") {
		return data;
	}
	if (
		typeof data === "object" &&
		data !== null &&
		"message" in data &&
		typeof data.message === "string"
	) {
		return data.message;
	}
	return "Κακό αίτημα";
};

export const isAxiosPrivateErrorHandled = (error: unknown) =>
	typeof error === "object" &&
	error !== null &&
	HANDLED_BY_AXIOS_PRIVATE in error &&
	(error as HandledAxiosError)[HANDLED_BY_AXIOS_PRIVATE] === true;

export const toastErrorUnlessHandled = (error: unknown, message: string) => {
	if (!isAxiosPrivateErrorHandled(error)) {
		toast.error(message);
	}
};

export const AxiosPrivateProvider = ({
	children,
}: {
	children: ReactNode;
}) => {
	const refresh = useRefreshToken();
	const { auth } = useAuth() as { auth: { accessToken?: string } };
	const isRefreshing = useRef(false);
	const newAccessToken = useRef("");
	const refreshRef = useRef(refresh);
	refreshRef.current = refresh;

	useLayoutEffect(() => {
		const requestIntercept = axiosPrivate.interceptors.request.use(
			(config) => {
				if (!config.headers.Authorization && auth?.accessToken) {
					config.headers.Authorization = `Bearer ${auth.accessToken}`;
				}
				return config;
			},
			(error) => Promise.reject(error),
		);

		const responseIntercept = axiosPrivate.interceptors.response.use(
			(response) => response,
			async (error: HandledAxiosError) => {
				const prevRequest = error.config;

				if (error.response?.status === 400) {
					error[HANDLED_BY_AXIOS_PRIVATE] = true;
					toast.error(getErrorMessage(error.response.data));
					return Promise.reject(error);
				}

				if (
					(error.response?.status === 401 ||
						error.response?.status === 403) &&
					prevRequest &&
					!(prevRequest as typeof prevRequest & { sent?: boolean }).sent &&
					!prevRequest.url?.includes("/refresh") &&
					!prevRequest.url?.includes("/login")
				) {
					const retryRequest = prevRequest as typeof prevRequest & {
						sent?: boolean;
					};

					if (!isRefreshing.current) {
						isRefreshing.current = true;
						retryRequest.sent = true;
						try {
							newAccessToken.current = await refreshRef.current();
							retryRequest.headers.Authorization =
								`Bearer ${newAccessToken.current}`;
							return axiosPrivate(retryRequest);
						} catch (refreshError) {
							console.error("Refresh failed, user will be logged out");
							return Promise.reject(refreshError);
						} finally {
							isRefreshing.current = false;
						}
					}

					let retryCount = 0;
					const maxRetries = 50;
					while (isRefreshing.current && retryCount < maxRetries) {
						await new Promise((resolve) => setTimeout(resolve, 100));
						retryCount++;
					}

					if (!isRefreshing.current && newAccessToken.current) {
						retryRequest.headers.Authorization =
							`Bearer ${newAccessToken.current}`;
						return axiosPrivate(retryRequest);
					}
				}

				return Promise.reject(error);
			},
		);

		return () => {
			axiosPrivate.interceptors.request.eject(requestIntercept);
			axiosPrivate.interceptors.response.eject(responseIntercept);
		};
	}, [auth?.accessToken]);

	return children;
};

const useAxiosPrivate = () => axiosPrivate;

export default useAxiosPrivate;
